import React, { useState, useEffect } from 'react';
import { BiSearch, BiPlus } from 'react-icons/bi';
import { BsBicycle } from 'react-icons/bs';
import { FaStar } from 'react-icons/fa6';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getRidersPending, getRiderStats, getRidersOverview, getAllRidersStats } from '../../redux/thunk';
import RiderDetailsModal from '../../components/modals/RiderDetailsModal';
import AddNewInternalRiderModal from '../../components/modals/AddNewInternalRiderModal';
import LoadingSpinner from '../../components/elements/LoadingSpinner';

interface Rider {
  _id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  status: string;
  isApproved: boolean;
  role: string;
  walletInfo: {
    currentBalance: number;
  };
  bankDetails?: Array<{
    _id: string;
    bankName: string;
    accountName: string;
    accountNumber: string;
    inUse: boolean;
  }>;
  createdAt: string;
  accountStatus: string;
}

const NewRiderManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    pendingRiders, 
    riderStats,
    ridersOverview,
    allRidersStats,
    status, 
    error
  } = useAppSelector((store) => store.logistics);
  
  const [selectedRider, setSelectedRider] = useState<Rider | null>(null);
  const [showRiderModal, setShowRiderModal] = useState(false);
  const [showAddRiderModal, setShowAddRiderModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  useEffect(() => {
    dispatch(getRidersPending());
    dispatch(getRiderStats());
    dispatch(getRidersOverview());
    dispatch(getAllRidersStats({ page: currentPage, limit: itemsPerPage }));
  }, [dispatch, currentPage, itemsPerPage]);

  const handleRefreshData = () => {
    dispatch(getRidersPending());
    dispatch(getRiderStats());
    dispatch(getRidersOverview());
    dispatch(getAllRidersStats({ page: currentPage, limit: itemsPerPage }));
  };

  // Get the current riders to display from the API data
  const currentRiders = allRidersStats?.data || [];
  
  // Filter riders based on search and filters
  const filteredRiders = currentRiders.filter((rider) => {
    const matchesSearch = 
      rider.fullName.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'All Statuses' || 
      rider.accountStatus.toLowerCase() === statusFilter.toLowerCase();
    
    const matchesLocation = locationFilter === 'All Locations' || 
      (rider.location && rider.location.toLowerCase().includes(locationFilter.toLowerCase()));
    
    return matchesSearch && matchesStatus && matchesLocation;
  });

  const handleViewRider = (rider: Rider) => {
    setSelectedRider(rider);
    setShowRiderModal(true);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Active';
      case 'suspended':
        return 'Suspended';  
      case 'inactive':
        return 'Inactive';
      default:
        return status;
    }
  };


  if (status === 'loading' && pendingRiders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Loading rider data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Rider Management</h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage rider applications, profiles, and status
              </p>
            </div>
            <button
              onClick={() => setShowAddRiderModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <BiPlus className="w-5 h-5" />
              Add New Rider
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
            <div className="text-blue-600 text-sm font-medium mb-1">External Riders</div>
            <div className="text-2xl font-semibold text-blue-800">{ridersOverview?.externalRiders || 0}</div>
          </div>
          <div className="bg-teal-50 rounded-lg p-6 border border-teal-200">
            <div className="text-teal-600 text-sm font-medium mb-1">Internal Riders</div>
            <div className="text-2xl font-semibold text-teal-800">{ridersOverview?.internalRiders || 0}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-6 border border-green-200">
            <div className="text-green-600 text-sm font-medium mb-1">Active Riders</div>
            <div className="text-2xl font-semibold text-green-800">{ridersOverview?.activeRiders || 0}</div>
          </div>
          <div className="bg-red-50 rounded-lg p-6 border border-red-200">
            <div className="text-red-600 text-sm font-medium mb-1">Pending Approvals</div>
            <div className="text-2xl font-semibold text-red-800">{ridersOverview?.pendingApproval || 0}</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1">
              <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search Riders"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="All Statuses">All Statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
              <select
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="All Locations">All Locations</option>
                <option value="Shomolu Axis">Shomolu Axis</option>
                <option value="Gbagada">Gbagada</option>
                <option value="Anthony">Anthony</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
            Error: {error}
          </div>
        )}

        {/* Riders Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Earnings</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRiders.map((rider, index) => (
                  <tr key={rider.riderId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                            <span className="text-teal-600 font-medium text-sm">
                              {rider.fullName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{rider.fullName}</div>
                          <div className="text-sm text-gray-500">ID: {rider.riderId.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        rider.riderType === 'Internal' 
                          ? 'bg-teal-100 text-teal-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {rider.riderType || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {rider.location || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {rider.averageRating ? (
                          <>
                            <span className="text-sm font-medium text-gray-900">{rider.averageRating}</span>
                            <FaStar className="w-4 h-4 text-yellow-400 fill-current ml-1" />
                          </>
                        ) : (
                          <span className="text-sm text-gray-500">No rating</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      ₦{rider.totalEarnings?.toLocaleString() || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(rider.accountStatus)}`}>
                        {getStatusText(rider.accountStatus)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => {
                          // Create a rider object compatible with the modal from API data
                          const modalRider: Rider = {
                            _id: rider.riderId,
                            fullName: rider.fullName,
                            email: '', // Not available in RiderStatItem
                            phoneNumber: '', // Not available in RiderStatItem
                            status: rider.accountStatus,
                            isApproved: true,
                            role: rider.riderType || 'rider',
                            walletInfo: { currentBalance: rider.totalEarnings || 0 },
                            createdAt: new Date().toISOString(),
                            accountStatus: rider.accountStatus
                          };
                          handleViewRider(modalRider);
                        }}
                        className="text-teal-600 hover:text-teal-900 font-medium text-sm border border-teal-600 px-3 py-1 rounded-lg hover:bg-teal-50 transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredRiders.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm || statusFilter !== 'All Statuses' || locationFilter !== 'All Locations'
                  ? "No riders found matching your criteria"
                  : "No riders available"
                }
              </p>
            </div>
          )}
          
          {/* Pagination Controls */}
          {allRidersStats && allRidersStats.totalPages > 1 && (
            <div className="bg-white px-6 py-3 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(currentPage + 1, allRidersStats.totalPages))}
                  disabled={currentPage === allRidersStats.totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(currentPage - 1) * itemsPerPage + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(currentPage * itemsPerPage, allRidersStats.totalRiders)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{allRidersStats.totalRiders}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, allRidersStats.totalPages) }, (_, i) => {
                      const pageNumber = i + 1;
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setCurrentPage(pageNumber)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === pageNumber
                              ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(Math.min(currentPage + 1, allRidersStats.totalPages))}
                      disabled={currentPage === allRidersStats.totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rider Details Modal */}
      {selectedRider && (
        <RiderDetailsModal
          rider={selectedRider}
          isOpen={showRiderModal}
          onClose={() => {
            setShowRiderModal(false);
            setSelectedRider(null);
          }}
          onUpdate={handleRefreshData}
        />
      )}

      {/* Add New Rider Modal */}
      <AddNewInternalRiderModal
        isOpen={showAddRiderModal}
        onClose={() => setShowAddRiderModal(false)}
        onSuccess={handleRefreshData}
      />
    </div>
  );
};

export default NewRiderManagement;